import { Download, FileText, FileSpreadsheet, FileJson, FileType, Loader2, Hash } from "lucide-react";
import { useExports } from "../../lib/queries";
import { downloadExport } from "../../lib/export";
import { GlassCard, SectionTag, Pill, Spinner, EmptyState } from "../../components/ui";
import { fmtTime, fmtNum } from "../../lib/format";
import type { ExportJob } from "../../types/api";

const FMT_ICON: Record<string, typeof FileText> = {
  csv: FileText,
  xlsx: FileSpreadsheet,
  json: FileJson,
  pdf: FileType,
};

const STATUS_COLOR: Record<string, string> = {
  pending: "#64748B",
  running: "#F59E0B",
  ready: "#2DD4BF",
  failed: "#EF4444",
};

export default function ExportHistory() {
  const { data, isLoading } = useExports();

  return (
    <div className="space-y-5">
      <div>
        <SectionTag>EXPORTS · DOWNLOAD CENTRE</SectionTag>
        <h1 className="text-xl font-semibold">Export History</h1>
        <p className="mt-1 text-sm text-slatey">
          Every export is generated asynchronously and stamped with the tenant, date range,
          filters applied, and an immutable audit reference.
        </p>
      </div>

      <GlassCard className="p-0">
        {isLoading ? (
          <div className="p-6"><Spinner /></div>
        ) : !data || data.length === 0 ? (
          <div className="p-6"><EmptyState>No exports yet — generate one from any list or case.</EmptyState></div>
        ) : (
          <div className="overflow-x-auto">
            <div className="min-w-[720px] divide-y divide-white/5">
              <div className="grid grid-cols-12 gap-2 px-4 py-2 text-[10px] uppercase tracking-wider text-slatey">
                <span className="col-span-4">Export</span>
                <span className="col-span-1">Format</span>
                <span className="col-span-1">Kind</span>
                <span className="col-span-1 text-right">Rows</span>
                <span className="col-span-2">Created</span>
                <span className="col-span-1">Audit</span>
                <span className="col-span-2 text-right">Status</span>
              </div>
              {data.map((job) => <Row key={job.id} job={job} />)}
            </div>
          </div>
        )}
      </GlassCard>
    </div>
  );
}

function Row({ job }: { job: ExportJob }) {
  const Icon = FMT_ICON[job.fmt] ?? FileText;
  return (
    <div className="grid grid-cols-12 items-center gap-2 px-4 py-2.5 text-xs hover:bg-white/[0.02]">
      <div className="col-span-4 flex items-center gap-2">
        <Icon size={15} className="text-aurora-cyan" />
        <span className="truncate font-medium">{job.title}</span>
      </div>
      <span className="col-span-1 mono uppercase text-slatey">{job.fmt}</span>
      <span className="col-span-1 text-slatey">{job.kind}</span>
      <span className="col-span-1 mono text-right">{job.row_count ? fmtNum(job.row_count) : "—"}</span>
      <span className="col-span-2 text-slatey">{fmtTime(job.created_at)}</span>
      <span className="col-span-1">
        {job.audit_ref ? (
          <span className="mono flex items-center gap-0.5 text-[10px] text-slate-500" title={job.audit_ref}>
            <Hash size={10} />{job.audit_ref.slice(0, 6)}
          </span>
        ) : "—"}
      </span>
      <span className="col-span-2 flex items-center justify-end gap-2">
        <Pill color={STATUS_COLOR[job.status]}>
          {job.status === "running" || job.status === "pending" ? (
            <Loader2 size={10} className="animate-spin" />
          ) : null}
          {job.status}
        </Pill>
        {job.status === "ready" && (
          <button
            onClick={() => downloadExport(job)}
            className="flex items-center gap-1 rounded-md border border-aurora-cyan/40 bg-aurora-cyan/10 px-2 py-1 text-aurora-cyan transition hover:bg-aurora-cyan/20"
          >
            <Download size={12} /> Get
          </button>
        )}
      </span>
    </div>
  );
}
